/**
 * Statement Uploads — "Statement Uploads" tab (Accounting Session 4, largest
 * piece). UI over the A3/Gate-2/Gate-3 extraction engine
 * (routes/extraction.py, services/extraction/*).
 *
 * Line review: confirm (approved) / reject / needs-info (with note) / pending,
 * plus void. Per-field review actions (harness, 2026-09): inline VALUE edit
 * (PUT /lines/{id}/fields/{field}) and a deliberate Ignore/Unignore toggle
 * (…/ignore with required reason, …/unignore) — an ignored field is a human
 * decision, distinct from missing/low-confidence, and no longer counts
 * against completeness. Every edit/ignore is audit-logged server-side.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { useAuth } from '@/contexts/AuthContext';
import {
  Upload, FileText, Loader2, Eye, EyeOff, CheckCircle2, XCircle, HelpCircle, Ban,
  ShieldAlert, Download, Settings2, Plus, X, Check, Pencil, RotateCcw, History,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  uploaded: { label: 'Processing', cls: 'bg-slate-100 text-slate-600' },
  detecting: { label: 'Processing', cls: 'bg-slate-100 text-slate-600' },
  processing: { label: 'Processing', cls: 'bg-slate-100 text-slate-600' },
  validating: { label: 'Processing', cls: 'bg-slate-100 text-slate-600' },
  pending_manual_redaction: { label: 'Redaction Needed', cls: 'bg-amber-100 text-amber-700' },
  review_queue: { label: 'Review Needed', cls: 'bg-rose-100 text-rose-700' },
  ready_to_post: { label: 'Verified', cls: 'bg-emerald-100 text-emerald-700' },
};
const REVIEW_META = {
  needs_review: { label: 'Needs Review', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  pending: { label: 'Pending', cls: 'bg-slate-50 text-slate-600 border-slate-200' },
  approved: { label: 'Approved', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  rejected: { label: 'Rejected', cls: 'bg-rose-50 text-rose-700 border-rose-200' },
  needs_info: { label: 'Needs Info', cls: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
};
const statusMeta = (s) => STATUS_META[s] || { label: s, cls: 'bg-slate-100 text-slate-600' };
const fmtDateTime = (d) => (d ? String(d).replace('T', ' ').slice(0, 16) : '—');

// ---------------------------------------------------------------------------
const UploadWidget = ({ profiles, onUploaded }) => {
  const [file, setFile] = useState(null);
  const [profileId, setProfileId] = useState('auto');
  const [uploading, setUploading] = useState(false);

  const submit = async () => {
    if (!file) { toast.error('Choose a file first'); return; }
    const fd = new FormData();
    fd.append('file', file);
    if (profileId !== 'auto') fd.append('profileId', profileId);
    setUploading(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/extraction/documents`, fd);
      toast.success(`${file.name} uploaded — ${statusMeta(res.data.status).label.toLowerCase()}`);
      setFile(null);
      onUploaded();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Card className="p-4 flex items-center gap-3 flex-wrap" data-testid="statement-upload-widget">
      <Input type="file" className="flex-1 min-w-[200px] h-9 text-xs" data-testid="statement-file-input"
        onChange={(e) => setFile(e.target.files?.[0] || null)} />
      <Select value={profileId} onValueChange={setProfileId}>
        <SelectTrigger className="w-56 h-9 text-xs" data-testid="statement-profile-select"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="auto">Auto-detect profile</SelectItem>
          {profiles.map((p) => <SelectItem key={p.profileId} value={p.profileId}>{p.name}</SelectItem>)}
        </SelectContent>
      </Select>
      <Button size="sm" className="bg-teal-600 hover:bg-teal-700 text-white" disabled={uploading} onClick={submit} data-testid="statement-upload-submit-btn">
        {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Upload className="h-4 w-4 mr-1" />Upload document</>}
      </Button>
    </Card>
  );
};

// ---------------------------------------------------------------------------
const ReviewNoteDialog = ({ line, status, onClose, onDone }) => {
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try {
      await axios.put(`${BACKEND_URL}/api/extraction/review-queue/${line.lineId}/status`, { status, note: note || null });
      toast.success(`Line marked ${REVIEW_META[status]?.label || status}`);
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to update line');
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="review-note-dialog">
        <DialogHeader>
          <DialogTitle>{REVIEW_META[status]?.label || status} this line</DialogTitle>
          <DialogDescription>Line {line.lineIndex} · reuses the existing review-queue status endpoint.</DialogDescription>
        </DialogHeader>
        <div><Label>Note (optional)</Label><Textarea data-testid="review-note-input" value={note} onChange={(e) => setNote(e.target.value)} rows={2} /></div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="review-note-submit-btn" className="bg-teal-600 hover:bg-teal-700 text-white">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Confirm'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// Per-field review cell — inline value edit + deliberate Ignore/Unignore
// toggle. Ignored fields render distinctly (greyed, struck value, "Ignored"
// badge + reason) so a human decision never looks like an extraction gap.
const FieldCell = ({ lineId, name, entry, required, disabled, onChanged }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const ignored = !!entry?.ignored;

  const startEdit = () => {
    setDraft(entry?.value == null ? '' : String(entry.value));
    setEditing(true);
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      await axios.put(`${BACKEND_URL}/api/extraction/lines/${lineId}/fields/${name}`, { value: draft });
      toast.success(`'${name}' updated`);
      setEditing(false);
      onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to update field');
    } finally {
      setSaving(false);
    }
  };

  const toggleIgnore = async () => {
    try {
      if (!ignored) {
        const reason = window.prompt(`Why is '${name}' not needed for this line? (required)`);
        if (!reason || !reason.trim()) { if (reason !== null) toast.error('A reason is required'); return; }
        await axios.put(`${BACKEND_URL}/api/extraction/lines/${lineId}/fields/${name}/ignore`, { reason });
        toast.success(`'${name}' marked as ignored`);
      } else {
        await axios.put(`${BACKEND_URL}/api/extraction/lines/${lineId}/fields/${name}/unignore`);
        toast.success(`'${name}' restored`);
      }
      onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to update field');
    }
  };

  if (editing) {
    return (
      <div className="text-xs bg-teal-50 border border-teal-200 rounded px-2 py-1" data-testid={`line-field-${lineId}-${name}`}>
        <div className="text-slate-400 mb-1">{name}{required && <span className="text-rose-500">*</span>}</div>
        <div className="flex items-center gap-1">
          <Input className="h-6 text-xs px-1.5 bg-white" value={draft} autoFocus
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') saveEdit(); if (e.key === 'Escape') setEditing(false); }}
            data-testid={`line-field-input-${lineId}-${name}`} />
          <button onClick={saveEdit} disabled={saving} title="Save" className="text-emerald-600 hover:text-emerald-700"
            data-testid={`line-field-save-${lineId}-${name}`}>
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
          </button>
          <button onClick={() => setEditing(false)} title="Cancel" className="text-slate-400 hover:text-slate-600"
            data-testid={`line-field-cancel-${lineId}-${name}`}><X className="h-3.5 w-3.5" /></button>
        </div>
      </div>
    );
  }

  return (
    <div className={`group relative text-xs rounded px-2 py-1 ${ignored ? 'bg-slate-100/70 border border-dashed border-slate-300' : 'bg-slate-50'}`}
      data-testid={`line-field-${lineId}-${name}`}>
      <div className="text-slate-400 flex items-center gap-1">
        {name}{required && <span className="text-rose-500">*</span>}
        {ignored && <span className="bg-slate-200 text-slate-500 text-[9px] font-semibold rounded px-1"
          data-testid={`line-field-ignored-badge-${lineId}-${name}`}>Ignored</span>}
      </div>
      <div className="font-medium truncate">
        <span className={ignored ? 'line-through text-slate-400' : 'text-slate-700'}>{entry?.value ?? '—'}</span>{' '}
        <span className="text-[10px] text-slate-400 font-normal">({entry?.confidence || '—'})</span>
      </div>
      {ignored && entry?.ignoreReason && (
        <div className="text-[10px] text-slate-400 italic truncate">reason: {entry.ignoreReason}</div>
      )}
      {!disabled && (
        <div className="absolute top-1 right-1 hidden group-hover:flex items-center gap-1 bg-white/90 rounded shadow-sm px-0.5">
          <button onClick={ignored ? toggleIgnore : startEdit} title={ignored ? 'Restore field' : 'Edit value'}
            className="text-slate-400 hover:text-teal-600 p-0.5" data-testid={`line-field-edit-btn-${lineId}-${name}`}>
            {ignored ? <RotateCcw className="h-3 w-3" /> : <Pencil className="h-3 w-3" />}
          </button>
          {!ignored && (
            <button onClick={toggleIgnore} title="Mark as not needed"
              className="text-slate-400 hover:text-amber-600 p-0.5" data-testid={`line-field-ignore-btn-${lineId}-${name}`}>
              <EyeOff className="h-3 w-3" />
            </button>
          )}
        </div>
      )}
    </div>
  );
};

const LineRow = ({ line, requiredFields = [], onChanged }) => {
  const [dialog, setDialog] = useState(null);
  const meta = REVIEW_META[line.reviewStatus] || REVIEW_META.pending;

  const voidLine = async () => {
    const reason = window.prompt('Reason to void this line (required):');
    if (!reason || !reason.trim()) { if (reason !== null) toast.error('A reason is required'); return; }
    try {
      await axios.delete(`${BACKEND_URL}/api/extraction/lines/${line.lineId}`, { data: { reason } });
      toast.success('Line voided');
      onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to void line');
    }
  };

  return (
    <Card className={`p-3 ${line.voided ? 'opacity-50' : ''}`} data-testid={`extraction-line-${line.lineId}`}>
      <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-400">Line {line.lineIndex}</span>
          <Badge className={`border text-[10px] ${meta.cls}`} data-testid={`line-review-status-${line.lineId}`}>{meta.label}</Badge>
          {line.voided && <Badge className="bg-rose-100 text-rose-700 text-[10px]">Voided</Badge>}
        </div>
        {!line.voided && (
          <div className="flex items-center gap-1.5">
            <Button size="sm" variant="outline" className="h-7 text-xs border-emerald-200 text-emerald-700 hover:bg-emerald-50"
              onClick={() => setDialog('approved')} data-testid={`line-approve-${line.lineId}`}>
              <CheckCircle2 className="h-3 w-3 mr-1" />Confirm
            </Button>
            <Button size="sm" variant="outline" className="h-7 text-xs border-rose-200 text-rose-700 hover:bg-rose-50"
              onClick={() => setDialog('rejected')} data-testid={`line-reject-${line.lineId}`}>
              <XCircle className="h-3 w-3 mr-1" />Reject
            </Button>
            <Button size="sm" variant="outline" className="h-7 text-xs border-indigo-200 text-indigo-700 hover:bg-indigo-50"
              onClick={() => setDialog('needs_info')} data-testid={`line-needs-info-${line.lineId}`}>
              <HelpCircle className="h-3 w-3 mr-1" />Needs Info
            </Button>
            <Button size="sm" variant="ghost" className="h-7 text-xs text-slate-400 hover:text-rose-600" onClick={voidLine} data-testid={`line-void-${line.lineId}`}>
              <Ban className="h-3 w-3" />
            </Button>
          </div>
        )}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {Object.entries(line.fields || {}).map(([name, entry]) => (
          <FieldCell key={name} lineId={line.lineId} name={name} entry={entry}
            required={requiredFields.includes(name)} disabled={!!line.voided} onChanged={onChanged} />
        ))}
      </div>
      {line.reviewNote && <p className="text-[11px] text-slate-400 mt-2">Note: {line.reviewNote}</p>}
      {dialog && <ReviewNoteDialog line={line} status={dialog} onClose={() => setDialog(null)} onDone={() => { setDialog(null); onChanged(); }} />}
    </Card>
  );
};

// ---------------------------------------------------------------------------
const DocumentDetailSheet = ({ documentId, onClose, onChanged }) => {
  const [doc, setDoc] = useState(null);
  const [requiredFields, setRequiredFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [redactFile, setRedactFile] = useState(null);
  const [submittingRedaction, setSubmittingRedaction] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const load = () => {
    setLoading(true);
    axios.get(`${BACKEND_URL}/api/extraction/documents/${documentId}`)
      .then((r) => {
        setDoc(r.data);
        if (r.data?.profileId) {
          axios.get(`${BACKEND_URL}/api/extraction/profiles/${r.data.profileId}`)
            .then((p) => setRequiredFields(p.data?.requiredFields || p.data?.expectedFields || []))
            .catch(() => setRequiredFields([]));
        } else {
          setRequiredFields([]);
        }
      })
      .catch(() => toast.error('Failed to load document'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [documentId]); // eslint-disable-line react-hooks/exhaustive-deps

  const downloadSource = async () => {
    setDownloading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/extraction/documents/${documentId}/source`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = doc?.fileName || 'source';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Failed to download source file');
    } finally {
      setDownloading(false);
    }
  };

  const submitRedaction = async () => {
    if (!redactFile) { toast.error('Choose the manually-redacted file first'); return; }
    const fd = new FormData();
    fd.append('file', redactFile);
    setSubmittingRedaction(true);
    try {
      await axios.post(`${BACKEND_URL}/api/extraction/documents/${documentId}/redaction/manual-complete`, fd);
      toast.success('Redacted file submitted — resuming AI extraction');
      setRedactFile(null);
      load();
      onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to submit redacted file');
    } finally {
      setSubmittingRedaction(false);
    }
  };

  return (
    <Sheet open onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-3xl overflow-y-auto" data-testid="document-detail-sheet">
        {loading || !doc ? (
          <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 text-teal-600 animate-spin" /></div>
        ) : (
          <div className="space-y-4">
            <SheetHeader>
              <SheetTitle className="flex items-center gap-2 flex-wrap">
                <FileText className="h-4 w-4" />{doc.fileName}
                <Badge className={statusMeta(doc.status).cls} data-testid="document-status-badge">{statusMeta(doc.status).label}</Badge>
              </SheetTitle>
              <SheetDescription>
                Uploaded by {doc.uploadedBy || '—'} · {fmtDateTime(doc.uploadedAt)} · format: {doc.formatDetected || 'detecting…'}
              </SheetDescription>
            </SheetHeader>

            {doc.duplicateOfDocumentId && (
              <div className="text-xs bg-amber-50 border border-amber-200 text-amber-700 rounded-lg p-2" data-testid="duplicate-warning">
                Possible duplicate of document {doc.duplicateOfDocumentId}
              </div>
            )}
            {doc.errorReason && (
              <div className="text-xs bg-rose-50 border border-rose-200 text-rose-700 rounded-lg p-2" data-testid="document-error-reason">{doc.errorReason}</div>
            )}

            {doc.status === 'pending_manual_redaction' && (
              <Card className="p-4 border-amber-200 bg-amber-50/40 space-y-3" data-testid="manual-redaction-panel">
                <div className="flex items-center gap-2 text-amber-800 text-sm font-semibold">
                  <ShieldAlert className="h-4 w-4" />Manual redaction required
                </div>
                <p className="text-xs text-amber-700">
                  OCR couldn't read enough of this page to trust that account/card numbers and names were fully found — it was
                  <b> not sent to AI unredacted</b>. Download the original, black out sensitive regions yourself in any tool, then
                  upload the redacted file to resume processing.
                </p>
                <Button size="sm" variant="outline" onClick={downloadSource} disabled={downloading} data-testid="download-source-btn">
                  {downloading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Download className="h-3.5 w-3.5 mr-1" />Download original</>}
                </Button>
                <div className="flex items-center gap-2">
                  <Input type="file" className="h-8 text-xs flex-1" data-testid="redacted-file-input"
                    onChange={(e) => setRedactFile(e.target.files?.[0] || null)} />
                  <Button size="sm" className="bg-amber-600 hover:bg-amber-700 text-white" disabled={submittingRedaction}
                    onClick={submitRedaction} data-testid="submit-redacted-file-btn">
                    {submittingRedaction ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Submit redacted file'}
                  </Button>
                </div>
              </Card>
            )}

            <details className="text-xs" data-testid="pipeline-trace-toggle">
              <summary className="cursor-pointer text-slate-500 flex items-center gap-1"><History className="h-3.5 w-3.5" />Pipeline trace</summary>
              <div className="mt-2 bg-slate-900 text-slate-100 rounded-lg p-3 font-mono max-h-40 overflow-y-auto" data-testid="pipeline-trace-log">
                {(doc.pipelineTrace || []).map((t, i) => (
                  <div key={i} className="mb-1">[{fmtDateTime(t.timestamp)}] {t.stage} — {t.status}: {t.detail}</div>
                ))}
              </div>
            </details>

            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
                Extracted lines ({(doc.lines || []).length})
              </div>
              <div className="space-y-2" data-testid="extraction-lines-list">
                {(doc.lines || []).map((l) => <LineRow key={l.lineId} line={l} requiredFields={requiredFields} onChanged={load} />)}
                {(doc.lines || []).length === 0 && (
                  <p className="text-xs text-slate-400" data-testid="no-lines-yet">
                    No lines extracted yet — this document may still be processing, or is awaiting manual redaction.
                  </p>
                )}
              </div>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

// ---------------------------------------------------------------------------
const ProfileFormDialog = ({ profile, onClose, onSaved }) => {
  const [form, setForm] = useState(() => ({
    name: profile?.name || '',
    institutionOrVendor: profile?.institutionOrVendor || '',
    documentType: profile?.documentType || 'bank_statement',
    expectedFormat: profile?.expectedFormat || 'pdf_digital',
    parserType: profile?.parserType || 'ai_vision',
    expectedFields: (profile?.expectedFields || []).join(', '),
    requiredFields: (profile?.requiredFields || []).join(', '),
    amountFields: (profile?.amountFields || []).join(', '),
  }));
  const [mapRows, setMapRows] = useState(
    Object.entries(profile?.fieldMapping || {}).map(([k, v]) => ({ from: k, to: v })).length
      ? Object.entries(profile?.fieldMapping || {}).map(([k, v]) => ({ from: k, to: v }))
      : [{ from: '', to: '' }],
  );
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!form.name.trim() || !form.institutionOrVendor.trim()) { toast.error('Name and institution/vendor are required'); return; }
    const fieldMapping = {};
    mapRows.forEach((r) => { if (r.from.trim()) fieldMapping[r.from.trim()] = r.to.trim(); });
    const body = {
      name: form.name.trim(), institutionOrVendor: form.institutionOrVendor.trim(),
      documentType: form.documentType, expectedFormat: form.expectedFormat, parserType: form.parserType,
      expectedFields: form.expectedFields.split(',').map((s) => s.trim()).filter(Boolean),
      requiredFields: form.requiredFields.split(',').map((s) => s.trim()).filter(Boolean),
      amountFields: form.amountFields.split(',').map((s) => s.trim()).filter(Boolean),
      fieldMapping,
    };
    setSaving(true);
    try {
      if (profile) {
        await axios.put(`${BACKEND_URL}/api/extraction/profiles/${profile.profileId}`, body);
        toast.success('Profile updated — new version active immediately');
      } else {
        await axios.post(`${BACKEND_URL}/api/extraction/profiles`, body);
        toast.success('Profile created — usable on the next upload immediately');
      }
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="profile-form-dialog" className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{profile ? 'Edit' : 'New'} extraction profile</DialogTitle>
          <DialogDescription>Pure config — never touches pipeline code. Usable on the very next upload.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Profile name</Label><Input data-testid="profile-name-input" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} /></div>
            <div><Label>Institution / vendor</Label><Input data-testid="profile-institution-input" value={form.institutionOrVendor} onChange={(e) => setForm((f) => ({ ...f, institutionOrVendor: e.target.value }))} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Document type</Label>
              <Select value={form.documentType} onValueChange={(v) => setForm((f) => ({ ...f, documentType: v }))}>
                <SelectTrigger data-testid="profile-doctype-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="bank_statement">Bank statement</SelectItem>
                  <SelectItem value="credit_card_statement">Credit card statement</SelectItem>
                  <SelectItem value="vendor_invoice">Vendor invoice / report</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Parser type</Label>
              <Select value={form.parserType} onValueChange={(v) => setForm((f) => ({ ...f, parserType: v }))}>
                <SelectTrigger data-testid="profile-parser-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="csv">CSV</SelectItem>
                  <SelectItem value="xlsx">XLSX</SelectItem>
                  <SelectItem value="pdf_table">PDF table</SelectItem>
                  <SelectItem value="ai_vision">AI vision</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div><Label>Expected format</Label><Input data-testid="profile-format-input" value={form.expectedFormat} onChange={(e) => setForm((f) => ({ ...f, expectedFormat: e.target.value }))} placeholder="pdf_digital / pdf_scanned / image / csv / xlsx" /></div>
          <div><Label>Expected fields (comma-separated)</Label><Input data-testid="profile-expected-fields-input" value={form.expectedFields} onChange={(e) => setForm((f) => ({ ...f, expectedFields: e.target.value }))} /></div>
          <div><Label>Required fields (comma-separated)</Label><Input data-testid="profile-required-fields-input" value={form.requiredFields} onChange={(e) => setForm((f) => ({ ...f, requiredFields: e.target.value }))} /></div>
          <div><Label>Amount fields (comma-separated)</Label><Input data-testid="profile-amount-fields-input" value={form.amountFields} onChange={(e) => setForm((f) => ({ ...f, amountFields: e.target.value }))} /></div>
          <div>
            <Label>Field mapping (source column → canonical field)</Label>
            <div className="space-y-2">
              {mapRows.map((row, i) => (
                <div key={i} className="grid grid-cols-[1fr_1fr_auto] gap-2" data-testid={`profile-map-row-${i}`}>
                  <Input placeholder="Source column" value={row.from} onChange={(e) => setMapRows((rs) => rs.map((r, j) => j === i ? { ...r, from: e.target.value } : r))} />
                  <Input placeholder="Canonical field" value={row.to} onChange={(e) => setMapRows((rs) => rs.map((r, j) => j === i ? { ...r, to: e.target.value } : r))} />
                  <Button type="button" variant="ghost" size="sm" onClick={() => setMapRows((rs) => rs.filter((_, j) => j !== i))}><X className="h-3.5 w-3.5" /></Button>
                </div>
              ))}
              <Button type="button" variant="outline" size="sm" data-testid="add-profile-map-row-btn" onClick={() => setMapRows((rs) => [...rs, { from: '', to: '' }])}>+ Add mapping</Button>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="profile-form-submit-btn" className="bg-teal-600 hover:bg-teal-700 text-white">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save profile'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ProfileManager = ({ profiles, onChanged }) => {
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  return (
    <Card className="p-4 mt-2" data-testid="profile-manager-section">
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm font-semibold text-slate-700 flex items-center gap-2">
          <Settings2 className="h-4 w-4" />Extraction Profiles (Admin)
        </div>
        <Button size="sm" onClick={() => { setEditing(null); setShowForm(true); }} data-testid="new-profile-btn">
          <Plus className="h-3.5 w-3.5 mr-1" />New profile
        </Button>
      </div>
      <div className="space-y-2" data-testid="profile-list">
        {profiles.map((p) => (
          <div key={p.profileId} className="flex items-center justify-between px-3 py-2 rounded-lg bg-slate-50 border border-slate-100" data-testid={`profile-row-${p.profileId}`}>
            <div className="text-xs">
              <span className="font-medium text-slate-700">{p.name}</span>
              <span className="text-slate-400"> · {p.institutionOrVendor} · {p.documentType} · {p.parserType} · v{p.version}</span>
            </div>
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => { setEditing(p); setShowForm(true); }} data-testid={`edit-profile-${p.profileId}`}>
              Edit
            </Button>
          </div>
        ))}
        {profiles.length === 0 && <p className="text-xs text-slate-400">No extraction profiles yet — uploads will rely on auto-detection.</p>}
      </div>
      {showForm && (
        <ProfileFormDialog profile={editing} onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); onChanged(); }} />
      )}
    </Card>
  );
};

// ---------------------------------------------------------------------------
const AccountingStatementUploads = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [documents, setDocuments] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedDocId, setSelectedDocId] = useState(null);
  const [showProfiles, setShowProfiles] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([
      axios.get(`${BACKEND_URL}/api/extraction/documents`),
      axios.get(`${BACKEND_URL}/api/extraction/profiles`),
    ]).then(([docsRes, profRes]) => {
      setDocuments(docsRes.data || []);
      setProfiles(profRes.data || []);
    }).catch(() => toast.error('Failed to load statement uploads'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const filtered = statusFilter === 'all' ? documents : documents.filter((d) => d.status === statusFilter);

  return (
    <div className="space-y-4" data-testid="accounting-statement-uploads-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-600 flex items-center gap-2">
            <Upload className="h-4 w-4 text-teal-700" />Statement Uploads
          </h2>
          <p className="text-xs text-slate-400 mt-0.5">Bank/card statements &amp; vendor invoices — OCR extraction, redaction and review, feeding the Books tab downstream.</p>
        </div>
        {isAdmin && (
          <Button size="sm" variant="outline" onClick={() => setShowProfiles((s) => !s)} data-testid="toggle-profile-manager-btn">
            <Settings2 className="h-3.5 w-3.5 mr-1" />{showProfiles ? 'Hide' : 'Show'} Profile Manager
          </Button>
        )}
      </div>

      {isAdmin && showProfiles && <ProfileManager profiles={profiles} onChanged={load} />}

      <UploadWidget profiles={profiles} onUploaded={load} />

      <Card className="p-3 flex items-center gap-2 flex-wrap" data-testid="documents-filter-bar">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-48 h-8 text-xs" data-testid="filter-document-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {Object.entries(STATUS_META).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </Card>

      {loading ? (
        <div className="flex justify-center py-16" data-testid="documents-loading"><Loader2 className="h-7 w-7 text-teal-600 animate-spin" /></div>
      ) : filtered.length === 0 ? (
        <Card className="p-12 text-center" data-testid="documents-empty-state">
          <FileText className="h-10 w-10 text-slate-200 mx-auto mb-3" />
          <p className="text-slate-500">No documents uploaded yet.</p>
        </Card>
      ) : (
        <div className="space-y-2" data-testid="documents-list">
          {filtered.map((d) => (
            <Card key={d.documentId} className="p-3 flex items-center justify-between gap-3 flex-wrap" data-testid={`document-row-${d.documentId}`}>
              <div className="flex items-center gap-3 min-w-0">
                <FileText className="h-4 w-4 text-slate-400 shrink-0" />
                <div className="min-w-0">
                  <div className="text-sm font-medium text-slate-800 truncate">{d.fileName}</div>
                  <div className="text-[11px] text-slate-400 truncate">
                    {d.formatDetected || 'detecting…'} · uploaded {fmtDateTime(d.uploadedAt)} by {d.uploadedBy || '—'}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Badge className={statusMeta(d.status).cls}>{statusMeta(d.status).label}</Badge>
                <Button size="sm" variant="outline" onClick={() => setSelectedDocId(d.documentId)} data-testid={`view-document-${d.documentId}`}>
                  <Eye className="h-3.5 w-3.5 mr-1" />View
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {selectedDocId && (
        <DocumentDetailSheet documentId={selectedDocId} onClose={() => setSelectedDocId(null)} onChanged={load} />
      )}
    </div>
  );
};

export default AccountingStatementUploads;
